import { useState } from "react";
import { toast } from "sonner";
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";

import {
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  TextArea,
  TextInput,
  Select,
  ErrorBanner,
} from "../crm/Form";
import TagInput from "./TagInput";
import { createTeamMember, ROLE_OPTIONS, PHONE_PLACEHOLDER, DAYS_OF_WEEK } from "../../lib/team";
import { formatApiError } from "../../lib/api";

const EMPTY_FORM = {
  name: "",
  email: "",
  role: "lead_tech",
  phone: "",
  certifications: [],
  service_regions: [],
  notes: "",
  scheduling_preferences: {
    preferred_start_time: "",
    preferred_end_time: "",
    max_jobs_per_day: null,
    travel_radius_miles: null,
    days_available: [],
    scheduling_notes: "",
  },
};

export default function AddTeamMemberModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showSched, setShowSched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setForm(EMPTY_FORM);
    setShowSched(false);
    setErr("");
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose && onClose();
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateSched = (k, v) =>
    setForm((f) => ({
      ...f,
      scheduling_preferences: { ...f.scheduling_preferences, [k]: v },
    }));

  const toggleDay = (d) => {
    setForm((f) => {
      const days = f.scheduling_preferences.days_available;
      const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d];
      return {
        ...f,
        scheduling_preferences: { ...f.scheduling_preferences, days_available: next },
      };
    });
  };

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) {
      setErr("Name is required.");
      return;
    }
    if (!form.email.trim()) {
      setErr("Email is required.");
      return;
    }
    setBusy(true);
    try {
      const sched = form.scheduling_preferences;
      const body = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        phone: form.phone.trim() || null,
        certifications: form.certifications,
        service_regions: form.service_regions,
        notes: form.notes.trim() || null,
        scheduling_preferences: {
          preferred_start_time: sched.preferred_start_time || null,
          preferred_end_time: sched.preferred_end_time || null,
          max_jobs_per_day: sched.max_jobs_per_day ?? null,
          travel_radius_miles: sched.travel_radius_miles ?? null,
          days_available: sched.days_available,
          scheduling_notes: sched.scheduling_notes.trim() || null,
        },
      };
      const created = await createTeamMember(body);
      toast.success(`Account created for ${created.name}`);
      reset();
      onCreated && onCreated(created);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add team member"
      subtitle="invite"
      data-testid="add-team-modal"
    >
      <div className="space-y-4">
        <ErrorBanner message={err} />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name" required>
            <TextInput
              value={form.name}
              onChange={(v) => update("name", v)}
              placeholder="Jane Tech"
              data-testid="add-team-name"
              maxLength={120}
            />
          </Field>
          <Field label="Email" required>
            <TextInput
              value={form.email}
              onChange={(v) => update("email", v)}
              type="email"
              placeholder="jane@zionsolar.com"
              data-testid="add-team-email"
            />
          </Field>
          <Field label="Role" required>
            <Select
              value={form.role}
              onChange={(v) => update("role", v || "lead_tech")}
              options={ROLE_OPTIONS}
              placeholder=""
              data-testid="add-team-role"
            />
          </Field>
          <Field label="Phone" hint="E.164 — used for SMS alerts">
            <TextInput
              value={form.phone}
              onChange={(v) => update("phone", v)}
              placeholder={PHONE_PLACEHOLDER}
              data-testid="add-team-phone"
            />
          </Field>
        </div>
        <Field label="Certifications">
          <TagInput
            value={form.certifications}
            onChange={(v) => update("certifications", v)}
            placeholder="e.g. NABCEP PV Associate, then press Enter"
            testidPrefix="add-team-cert"
          />
        </Field>
        <Field label="Service regions">
          <TagInput
            value={form.service_regions}
            onChange={(v) => update("service_regions", v)}
            placeholder="e.g. Salt Lake, then press Enter"
            testidPrefix="add-team-region"
          />
        </Field>
        <Field label="Internal notes" hint="Owner-only. Max 1000 chars.">
          <TextArea
            value={form.notes}
            onChange={(v) => update("notes", v)}
            rows={3}
            maxLength={1000}
            data-testid="add-team-notes"
          />
        </Field>

        {/* Collapsible scheduling section */}
        <button
          type="button"
          onClick={() => setShowSched((s) => !s)}
          data-testid="add-team-sched-toggle"
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 select-none"
        >
          {showSched ? <CaretUpIcon size={12} /> : <CaretDownIcon size={12} />}
          Scheduling preferences
        </button>
        {showSched && (
          <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/40 p-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Start time">
                <input
                  type="time"
                  value={form.scheduling_preferences.preferred_start_time || ""}
                  onChange={(e) => updateSched("preferred_start_time", e.target.value || null)}
                  data-testid="add-team-sched-start"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
              </Field>
              <Field label="End time">
                <input
                  type="time"
                  value={form.scheduling_preferences.preferred_end_time || ""}
                  onChange={(e) => updateSched("preferred_end_time", e.target.value || null)}
                  data-testid="add-team-sched-end"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
              </Field>
              <Field label="Max jobs / day">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={form.scheduling_preferences.max_jobs_per_day ?? ""}
                  onChange={(e) =>
                    updateSched(
                      "max_jobs_per_day",
                      e.target.value === "" ? null : Number(e.target.value)
                    )
                  }
                  data-testid="add-team-sched-max-jobs"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
              </Field>
              <Field label="Travel radius (mi)">
                <input
                  type="number"
                  min={0}
                  max={500}
                  value={form.scheduling_preferences.travel_radius_miles ?? ""}
                  onChange={(e) =>
                    updateSched(
                      "travel_radius_miles",
                      e.target.value === "" ? null : Number(e.target.value)
                    )
                  }
                  data-testid="add-team-sched-radius"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
              </Field>
            </div>
            <Field label="Days available">
              <div className="flex flex-wrap gap-1.5">
                {DAYS_OF_WEEK.map((d) => {
                  const active = form.scheduling_preferences.days_available.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleDay(d.value)}
                      data-testid={`add-team-sched-day-${d.value}`}
                      className={`text-xs px-3 py-1 rounded-md border transition ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {d.short}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Scheduling notes">
              <TextArea
                value={form.scheduling_preferences.scheduling_notes}
                onChange={(v) => updateSched("scheduling_notes", v)}
                rows={2}
                maxLength={500}
                data-testid="add-team-sched-notes"
              />
            </Field>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={close} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="add-team-submit"
          >
            {busy ? "Creating…" : "Create account"}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
